import { Color } from './java/Color';
import { Graphics2D, Font, FontMetrics, RenderingHints } from './java/Graphics2D';
import { Polygon } from './java/Geom';
import { MouseEvent } from './java/AwtEvent';
import { STATE } from './STATE';
import { Game } from './Game';
import { PageRenderer } from './PageRenderer';
import { AudioPlayer } from './AudioPlayer';
import { Settings } from './Settings';

type Track = ReturnType<typeof AudioPlayer.getCurrentTrack>;

// Drag state
enum DragTarget { NONE, PROGRESS, VOLUME }

export class MusicPlayer {

  // Dynamic positions
  private static cx(): number { return Math.trunc(Game.WIDTH / 2); }
  private static progX(): number { return Math.trunc((Game.WIDTH - MusicPlayer.PROG_W) / 2); }
  private static volX(): number { return Math.trunc((Game.WIDTH - MusicPlayer.VOL_W) / 2); }

  // Fixed dimensions
  private static readonly PROG_Y = 430;
  private static readonly PROG_W = 800;
  private static readonly PROG_H = 5;
  private static readonly PROG_KNOB = 14;
  private static readonly CTRL_Y = 490;
  private static readonly CTRL_SIZE = 44;
  private static readonly PLAY_SIZE = 56;
  private static readonly VOL_Y = 580;
  private static readonly VOL_W = 300;
  private static readonly VOL_H = 5;
  private static readonly VOL_KNOB = 12;
  private static readonly DRAG_MARGIN = 16;
  private static readonly CTRL_SPACING = 80;

  // Button indices
  private static readonly BTN_PREV = 0;
  private static readonly BTN_PLAY = 1;
  private static readonly BTN_NEXT = 2;
  private static readonly BTN_BACK = 3;
  private static readonly BTN_COUNT = 4;

  // Hover animation state
  private hover: number[] = new Array<number>(MusicPlayer.BTN_COUNT).fill(0);
  private progBarHover = 0;
  private volBarHover = 0;

  // Mouse tracking
  private mouseX = 0;
  private mouseY = 0;

  private dragging: DragTarget = DragTarget.NONE;

  // Hover colors
  private static get CTRL_BG(): Color { return PageRenderer.SURFACE_LIGHT; }
  private static readonly CTRL_BG_HOVER = new Color(48, 62, 82);
  private static get CTRL_ICON(): Color { return PageRenderer.TEXT_SEC; }
  private static get CTRL_ICON_HOVER(): Color { return PageRenderer.TEXT; }
  private static get PLAY_BG_ACTIVE(): Color { return PageRenderer.ACCENT; }
  private static readonly PLAY_BG_ACTIVE_HOVER = new Color(110, 225, 218);

  public tick(): void {
    // Music auto-advance is handled globally in Game.tick()

    if (Game.gameState !== STATE.MusicPlayer) return;

    // Update hover animations
    const targets: boolean[] = this.getHoverTargets();
    for (let i = 0; i < MusicPlayer.BTN_COUNT; i++) {
      const target = targets[i] ? 1 : 0;
      this.hover[i] += (target - this.hover[i]) * 0.14;
      if (Math.abs(this.hover[i] - target) < 0.01) this.hover[i] = target;
    }

    // Bar hover
    const onProg = this.dragging === DragTarget.PROGRESS
      || this.hitBar(this.mouseX, this.mouseY, MusicPlayer.progX(), MusicPlayer.PROG_Y, MusicPlayer.PROG_W);
    const onVol = this.dragging === DragTarget.VOLUME
      || this.hitBar(this.mouseX, this.mouseY, MusicPlayer.volX(), MusicPlayer.VOL_Y, MusicPlayer.VOL_W);
    this.progBarHover += ((onProg ? 1 : 0) - this.progBarHover) * 0.14;
    this.volBarHover += ((onVol ? 1 : 0) - this.volBarHover) * 0.14;
  }

  private getHoverTargets(): boolean[] {
    const t: boolean[] = new Array<boolean>(MusicPlayer.BTN_COUNT).fill(false);
    const cxv = MusicPlayer.cx();
    const playCy = MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2)
      - Math.trunc((MusicPlayer.PLAY_SIZE - MusicPlayer.CTRL_SIZE) / 2) + Math.trunc(MusicPlayer.PLAY_SIZE / 2);
    t[MusicPlayer.BTN_PREV] = this.inCircle(this.mouseX, this.mouseY, cxv - MusicPlayer.CTRL_SPACING,
      MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2), Math.trunc(MusicPlayer.CTRL_SIZE / 2) + 4);
    t[MusicPlayer.BTN_PLAY] = this.inCircle(this.mouseX, this.mouseY, cxv, playCy, Math.trunc(MusicPlayer.PLAY_SIZE / 2) + 4);
    t[MusicPlayer.BTN_NEXT] = this.inCircle(this.mouseX, this.mouseY, cxv + MusicPlayer.CTRL_SPACING,
      MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2), Math.trunc(MusicPlayer.CTRL_SIZE / 2) + 4);
    t[MusicPlayer.BTN_BACK] = this.mouseX >= PageRenderer.backX() && this.mouseX <= PageRenderer.backX() + PageRenderer.BACK_W
      && this.mouseY >= PageRenderer.BACK_Y && this.mouseY <= PageRenderer.BACK_Y + PageRenderer.BACK_H;
    return t;
  }

  // ==================== Rendering ====================

  public render(g: Graphics2D): void {
    const g2 = g;
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);
    g2.setRenderingHint(RenderingHints.KEY_TEXT_ANTIALIASING, RenderingHints.VALUE_TEXT_ANTIALIAS_ON);

    PageRenderer.drawBackground(g2);
    PageRenderer.drawTitle(g2, 'Music Player');

    // Back button with hover
    const bx = PageRenderer.backX();
    g2.setColor(this.lerp(PageRenderer.SURFACE, MusicPlayer.CTRL_BG_HOVER, this.hover[MusicPlayer.BTN_BACK]));
    g2.fillRoundRect(bx, PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H, 8, 8);
    g2.setColor(this.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, this.hover[MusicPlayer.BTN_BACK] * 0.4));
    g2.drawRoundRect(bx, PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H, 8, 8);
    g2.setFont(PageRenderer.SMALL_FONT);
    g2.setColor(this.lerp(PageRenderer.TEXT_SEC, PageRenderer.TEXT, this.hover[MusicPlayer.BTN_BACK]));
    PageRenderer.drawCenteredString(g2, 'Back', bx, PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H);

    // Album art
    const artSize = 180;
    const artX = MusicPlayer.cx() - Math.trunc(artSize / 2);
    const artY = 130;
    PageRenderer.drawPanel(g2, artX, artY, artSize, artSize);
    g2.setFont(new Font('Arial', Font.BOLD, 60));
    g2.setColor(PageRenderer.ACCENT);
    let fm: FontMetrics = g2.getFontMetrics();
    g2.drawString('♫', MusicPlayer.cx() - Math.trunc(fm.stringWidth('♫') / 2), artY + Math.trunc(artSize / 2) + 20);

    if (AudioPlayer.getTrackCount() > 0) {
      const track: Track = AudioPlayer.getCurrentTrack();

      // Track name
      g2.setFont(PageRenderer.HEADING_FONT);
      g2.setColor(PageRenderer.TEXT);
      fm = g2.getFontMetrics();
      g2.drawString(track.displayName, MusicPlayer.cx() - Math.trunc(fm.stringWidth(track.displayName) / 2), 365);

      // Track number
      g2.setFont(PageRenderer.SMALL_FONT);
      g2.setColor(PageRenderer.TEXT_MUTED);
      const info = 'Track ' + (AudioPlayer.getCurrentTrackIndex() + 1) + ' of ' + AudioPlayer.getTrackCount();
      fm = g2.getFontMetrics();
      g2.drawString(info, MusicPlayer.cx() - Math.trunc(fm.stringWidth(info) / 2), 390);

      // Progress bar
      this.renderProgressBar(g2, track);
    } else {
      g2.setFont(PageRenderer.HEADING_FONT);
      g2.setColor(PageRenderer.TEXT_MUTED);
      fm = g2.getFontMetrics();
      const msg = 'No tracks loaded';
      g2.drawString(msg, MusicPlayer.cx() - Math.trunc(fm.stringWidth(msg) / 2), 375);
    }

    this.renderControls(g2);
    this.renderVolumeBar(g2);
  }

  private renderProgressBar(g: Graphics2D, track: Track): void {
    const pos = AudioPlayer.getPosition();
    const dur = track.duration;
    const progress = (dur > 0) ? Math.min(pos / dur, 1) : 0;
    const px = MusicPlayer.progX();

    // Bar background
    g.setColor(this.lerp(PageRenderer.BORDER, new Color(55, 70, 90), this.progBarHover));
    g.fillRoundRect(px, MusicPlayer.PROG_Y, MusicPlayer.PROG_W, MusicPlayer.PROG_H, 3, 3);

    // Filled
    const filledW = Math.trunc(MusicPlayer.PROG_W * progress);
    if (filledW > 0) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(px, MusicPlayer.PROG_Y, filledW, MusicPlayer.PROG_H, 3, 3);
    }

    // Knob — grows slightly on hover
    const knobSize = MusicPlayer.PROG_KNOB + Math.trunc(4 * this.progBarHover);
    g.setColor(this.lerp(PageRenderer.TEXT, PageRenderer.ACCENT, this.progBarHover));
    g.fillOval(px + filledW - Math.trunc(knobSize / 2),
      MusicPlayer.PROG_Y + Math.trunc(MusicPlayer.PROG_H / 2) - Math.trunc(knobSize / 2), knobSize, knobSize);

    // Time labels
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString(this.formatTime(pos), px, MusicPlayer.PROG_Y + 25);
    const durStr = this.formatTime(dur);
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(durStr, px + MusicPlayer.PROG_W - fm.stringWidth(durStr), MusicPlayer.PROG_Y + 25);
  }

  private renderControls(g: Graphics2D): void {
    const cxv = MusicPlayer.cx();
    const prevX = cxv - MusicPlayer.CTRL_SPACING;
    const nextX = cxv + MusicPlayer.CTRL_SPACING;
    const cy = MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2);

    // Previous
    this.drawCtrlCircle(g, prevX, MusicPlayer.CTRL_Y, MusicPlayer.CTRL_SIZE, this.hover[MusicPlayer.BTN_PREV], false);
    this.drawPrevIcon(g, prevX, cy, this.hover[MusicPlayer.BTN_PREV]);

    // Play/Pause
    const playing = AudioPlayer.isPlaying();
    const py = MusicPlayer.CTRL_Y - Math.trunc((MusicPlayer.PLAY_SIZE - MusicPlayer.CTRL_SIZE) / 2);
    const playCy = py + Math.trunc(MusicPlayer.PLAY_SIZE / 2);
    if (playing) {
      g.setColor(this.lerp(MusicPlayer.PLAY_BG_ACTIVE, MusicPlayer.PLAY_BG_ACTIVE_HOVER, this.hover[MusicPlayer.BTN_PLAY]));
    } else {
      g.setColor(this.lerp(MusicPlayer.CTRL_BG, MusicPlayer.CTRL_BG_HOVER, this.hover[MusicPlayer.BTN_PLAY]));
    }
    g.fillOval(cxv - Math.trunc(MusicPlayer.PLAY_SIZE / 2), py, MusicPlayer.PLAY_SIZE, MusicPlayer.PLAY_SIZE);
    if (!playing) {
      g.setColor(this.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, this.hover[MusicPlayer.BTN_PLAY] * 0.5));
      g.drawOval(cxv - Math.trunc(MusicPlayer.PLAY_SIZE / 2), py, MusicPlayer.PLAY_SIZE, MusicPlayer.PLAY_SIZE);
    }
    const iconCol: Color = playing
      ? PageRenderer.BG_DARK
      : this.lerp(MusicPlayer.CTRL_ICON, PageRenderer.ACCENT, this.hover[MusicPlayer.BTN_PLAY]);
    if (playing) {
      this.drawPauseIcon(g, cxv, playCy, iconCol);
    } else {
      this.drawPlayIcon(g, cxv, playCy, iconCol);
    }

    // Next
    this.drawCtrlCircle(g, nextX, MusicPlayer.CTRL_Y, MusicPlayer.CTRL_SIZE, this.hover[MusicPlayer.BTN_NEXT], false);
    this.drawNextIcon(g, nextX, cy, this.hover[MusicPlayer.BTN_NEXT]);
  }

  private drawCtrlCircle(g: Graphics2D, cx: number, cy: number, size: number, h: number, _active: boolean): void {
    g.setColor(this.lerp(MusicPlayer.CTRL_BG, MusicPlayer.CTRL_BG_HOVER, h));
    g.fillOval(cx - Math.trunc(size / 2), cy, size, size);
    g.setColor(this.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, h * 0.4));
    g.drawOval(cx - Math.trunc(size / 2), cy, size, size);
  }

  private drawPlayIcon(g: Graphics2D, cx: number, cy: number, c: Color): void {
    g.setColor(c);
    const tri = new Polygon(
      [cx - 7, cx - 7, cx + 10],
      [cy - 12, cy + 12, cy], 3);
    g.fillPolygon(tri.xpoints, tri.ypoints, tri.npoints);
  }

  private drawPauseIcon(g: Graphics2D, cx: number, cy: number, c: Color): void {
    g.setColor(c);
    g.fillRoundRect(cx - 9, cy - 10, 6, 20, 2, 2);
    g.fillRoundRect(cx + 3, cy - 10, 6, 20, 2, 2);
  }

  private drawPrevIcon(g: Graphics2D, cx: number, cy: number, h: number): void {
    g.setColor(this.lerp(MusicPlayer.CTRL_ICON, MusicPlayer.CTRL_ICON_HOVER, h));
    // Bar
    g.fillRect(cx - 9, cy - 7, 3, 14);
    // Triangle
    const tri = new Polygon(
      [cx + 8, cx - 4, cx + 8],
      [cy - 7, cy, cy + 7], 3);
    g.fillPolygon(tri.xpoints, tri.ypoints, tri.npoints);
  }

  private drawNextIcon(g: Graphics2D, cx: number, cy: number, h: number): void {
    g.setColor(this.lerp(MusicPlayer.CTRL_ICON, MusicPlayer.CTRL_ICON_HOVER, h));
    // Triangle
    const tri = new Polygon(
      [cx - 8, cx + 4, cx - 8],
      [cy - 7, cy, cy + 7], 3);
    g.fillPolygon(tri.xpoints, tri.ypoints, tri.npoints);
    // Bar
    g.fillRect(cx + 6, cy - 7, 3, 14);
  }

  private renderVolumeBar(g: Graphics2D): void {
    const vol = AudioPlayer.getVolume();
    const vx = MusicPlayer.volX();

    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString('♪', vx - 22, MusicPlayer.VOL_Y + 5);
    g.drawString('♫', vx + MusicPlayer.VOL_W + 10, MusicPlayer.VOL_Y + 5);

    const pct = Math.round(vol * 100) + '%';
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(pct, MusicPlayer.cx() - Math.trunc(fm.stringWidth(pct) / 2), MusicPlayer.VOL_Y + 28);

    // Bar background
    g.setColor(this.lerp(PageRenderer.BORDER, new Color(55, 70, 90), this.volBarHover));
    g.fillRoundRect(vx, MusicPlayer.VOL_Y, MusicPlayer.VOL_W, MusicPlayer.VOL_H, 3, 3);

    // Filled
    const filledW = Math.trunc(MusicPlayer.VOL_W * vol);
    if (filledW > 0) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(vx, MusicPlayer.VOL_Y, filledW, MusicPlayer.VOL_H, 3, 3);
    }

    // Knob
    const knobSize = MusicPlayer.VOL_KNOB + Math.trunc(3 * this.volBarHover);
    g.setColor(this.lerp(PageRenderer.TEXT, PageRenderer.ACCENT, this.volBarHover));
    g.fillOval(vx + filledW - Math.trunc(knobSize / 2),
      MusicPlayer.VOL_Y + Math.trunc(MusicPlayer.VOL_H / 2) - Math.trunc(knobSize / 2), knobSize, knobSize);
  }

  // ==================== Input ====================

  public mousePressed(e: MouseEvent): void {
    if (Game.gameState !== STATE.MusicPlayer) return;

    const mx = Game.toGameX(e.getX());
    const my = Game.toGameY(e.getY());

    // Back
    if (mx >= PageRenderer.backX() && mx <= PageRenderer.backX() + PageRenderer.BACK_W
      && my >= PageRenderer.BACK_Y && my <= PageRenderer.BACK_Y + PageRenderer.BACK_H) {
      Game.gameState = STATE.Menu;
      return;
    }

    if (this.hitBar(mx, my, MusicPlayer.progX(), MusicPlayer.PROG_Y, MusicPlayer.PROG_W)) {
      this.dragging = DragTarget.PROGRESS;
      this.applyProgress(mx);
      return;
    }

    if (this.hitBar(mx, my, MusicPlayer.volX(), MusicPlayer.VOL_Y, MusicPlayer.VOL_W)) {
      this.dragging = DragTarget.VOLUME;
      this.applyVolume(mx);
      return;
    }

    const cxv = MusicPlayer.cx();
    const playCy = MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2)
      - Math.trunc((MusicPlayer.PLAY_SIZE - MusicPlayer.CTRL_SIZE) / 2) + Math.trunc(MusicPlayer.PLAY_SIZE / 2);
    if (this.inCircle(mx, my, cxv, playCy, Math.trunc(MusicPlayer.PLAY_SIZE / 2))) {
      AudioPlayer.togglePlayPause(); return;
    }
    if (this.inCircle(mx, my, cxv - MusicPlayer.CTRL_SPACING,
      MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2), Math.trunc(MusicPlayer.CTRL_SIZE / 2))) {
      AudioPlayer.previousTrack(); return;
    }
    if (this.inCircle(mx, my, cxv + MusicPlayer.CTRL_SPACING,
      MusicPlayer.CTRL_Y + Math.trunc(MusicPlayer.CTRL_SIZE / 2), Math.trunc(MusicPlayer.CTRL_SIZE / 2))) {
      AudioPlayer.nextTrack(); return;
    }
  }

  public mouseDragged(e: MouseEvent): void {
    if (Game.gameState !== STATE.MusicPlayer) return;
    this.mouseX = Game.toGameX(e.getX());
    this.mouseY = Game.toGameY(e.getY());
    if (this.dragging === DragTarget.PROGRESS) this.applyProgress(this.mouseX);
    else if (this.dragging === DragTarget.VOLUME) this.applyVolume(this.mouseX);
  }

  public mouseMoved(e: MouseEvent): void {
    this.mouseX = Game.toGameX(e.getX());
    this.mouseY = Game.toGameY(e.getY());
  }

  public mouseReleased(_e: MouseEvent): void {
    this.dragging = DragTarget.NONE;
  }

  // ==================== Helpers ====================

  private applyProgress(mx: number): void {
    if (AudioPlayer.getTrackCount() > 0 && !AudioPlayer.isStopped()) {
      AudioPlayer.seekTo(this.clampRatio(mx, MusicPlayer.progX(), MusicPlayer.PROG_W) * AudioPlayer.getCurrentTrack().duration);
    }
  }

  private applyVolume(mx: number): void {
    const vol = this.clampRatio(mx, MusicPlayer.volX(), MusicPlayer.VOL_W);
    Settings.setMusicVolume(vol);
  }

  private clampRatio(mx: number, barX: number, barW: number): number {
    return Math.max(0, Math.min(1, (mx - barX) / barW));
  }

  private hitBar(mx: number, my: number, barX: number, barY: number, barW: number): boolean {
    return mx >= barX - MusicPlayer.DRAG_MARGIN && mx <= barX + barW + MusicPlayer.DRAG_MARGIN
      && my >= barY - MusicPlayer.DRAG_MARGIN && my <= barY + MusicPlayer.PROG_H + MusicPlayer.DRAG_MARGIN;
  }

  private inCircle(mx: number, my: number, cx: number, cy: number, r: number): boolean {
    return (mx - cx) * (mx - cx) + (my - cy) * (my - cy) <= r * r;
  }

  private lerp(a: Color, b: Color, t: number): Color {
    t = Math.max(0, Math.min(1, t));
    return new Color(
      Math.trunc(a.getRed() + (b.getRed() - a.getRed()) * t),
      Math.trunc(a.getGreen() + (b.getGreen() - a.getGreen()) * t),
      Math.trunc(a.getBlue() + (b.getBlue() - a.getBlue()) * t),
      Math.trunc(a.getAlpha() + (b.getAlpha() - a.getAlpha()) * t));
  }

  private formatTime(seconds: number): string {
    const total = Math.trunc(seconds);
    const m = Math.trunc(total / 60);
    const s = total % 60;
    return `${m}:${s < 10 ? '0' + s : s}`;
  }
}
